<template>
  <div v-if="$store.account.account">
    <header class="bg-white">
      <div class="flex w-screen n-max-width h-80 overflow-y-hidden relative">
        <div class="z-10 my-auto grow">
          <h1 class="text-5xl py-6 font-medium leading-tight text-left text-gray-900 z-10 my-auto grow">
            <span class="bg-white">
              Bienvenue <br />
              <span class="font-bold text-blue">
                {{ $store.account.account.firstname }} {{ $store.account.account.lastname }}
              </span>
            </span>
          </h1>
          <span v-if="$store.trainee.contactPackagesWaitingAttendance.length && $store.account.account.moodleSuspended"
            class="bg-white text-orange-400">
            Votre accès à la plateforme de cours est actuellement suspendu. Validez votre feuille d'émargement pour
            rétablir l'accès.
          </span>
        </div>
        <img src="~/assets/images/bg_home.jpg" class="h-80 absolute right-0"
          style="clip-path: polygon(0px 100%, 100% 100%, 100% 0px, 20% 0px);" />
      </div>
    </header>

    <main class="bg-white" v-if="$rights.isTrainee">

      <!-- Trainings -->
      <section class="bg-gray-50 pt-10 pb-4">
        <div class="n-max-width">
          <h2 class="text-3xl font-extrabold text-gray-900 sm:text-5xl sm:leading-none sm:tracking-tight lg:text-6xl">
            Mes formations
          </h2>
          <p class="mt-6 max-w-2xl text-xl text-gray-500">Formations suivies et formations en projet.</p>

          <div v-if="packages.length" class="mt-10 sm:mt-14 space-y-12 lg:space-y-0 lg:grid lg:gap-8"
            :class="packages.length > 2 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'">
            <div v-for="pack in packages" :key="pack.package.id"
              class="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col">
              <div class="flex-1">
                <h3 class="text-2xl font-extrabold text-gray-900">{{ pack.package.name }}</h3>
                <p class="absolute top-0 py-1.5 px-4 rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2"
                  :class="{ 'bg-blue-500 text-white': pack.status === $enum.TrainingStatus.NEXT, 'bg-purple-500 text-white': pack.status === $enum.TrainingStatus.WIP, 'bg-green text-blue': pack.status === $enum.TrainingStatus.ENDED }">
                  {{ $enum.TrainingStatus.translate(pack.status) }}
                </p>
                <p v-if="pack.package.goals?.length" class="mt-6 text-gray-500">
                  Objectifs de formation :
                </p>

                <ul role="list" class="mt-6 space-y-3">
                  <li v-for="feature in pack.package.goals" :key="feature" class="flex">
                    <CheckIcon class="flex-shrink-0 w-6 h-6 text-blue-500" aria-hidden="true" />
                    <span class="ml-3 text-gray-500">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <NuxtLink :to="`/training/${pack.contactPackage.id}`" class="button button-blue w-full py-3 px-6 mt-8">
                Accéder à ma formation
              </NuxtLink>
            </div>
          </div>
          <div v-else class="w-full h-full py-10 my-10 border-y flex items-center justify-center text-gray-500">
            Vous n'avez pas encore de formation
          </div>

        </div>
      </section>

      <!-- Comments -->
      <section class="pt-10 pb-4">
        <div class="n-max-width">
          <h2 class="text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900">
            Derniers commentaires
          </h2>
          <p class="mt-3 text-xl text-gray-500 sm:mt-4 ">
            Derniers échanges avec votre conseiller et votre référent métier.
          </p>

          <template v-if="comments">
            <div v-if="comments.length" class="mt-10 sm:mt-14 grid gap-16 lg:grid-cols-3 lg:gap-x-5 lg:gap-y-12">
              <CommentTrainee v-for="comment in comments" :key="comment.id" :model-value="comment" truncate />
            </div>
            <div v-else class="w-full h-full my-10 pt-10 border-t flex items-center justify-center text-gray-500">
              Vous n'avez pas encore de commentaires
            </div>
            <nav
              class="mt-12 bg-white px-4 py-3 flex flex-wrap sm:flex-nowrap items-center justify-between border-t border-gray-200 sm:px-6"
              aria-label="Pagination">
              <div class="w-full sm:w-1/2 mb-4 sm:mb-0" v-if="comments.length">
                <p class="text-sm text-gray-700">
                  Échanges
                  <span class="font-medium">{{ Math.min(commentsOffset + 1, commentsCount) }}</span>
                  à
                  <span class="font-medium">{{ Math.min(commentsOffset + 3, commentsCount) }}</span>
                  sur
                  <span class="font-medium">{{ commentsCount }}</span>
                </p>
              </div>
              <div class="flex-1 flex justify-between sm:justify-end" v-if="comments.length">
                <button @click="commentsOffset -= 3" :disabled="commentsOffset === 0"
                  class="button button-default px-2 py-1 mr-2">
                  Suivant
                </button>
                <button @click="commentsOffset += 3" :disabled="(commentsOffset + 3) >= commentsCount"
                  class="button button-default px-2 py-1">
                  Précédent
                </button>
              </div>
            </nav>
          </template>

        </div>
      </section>

      <!-- Contacts -->
      <section class="bg-gray-50 pt-10 pb-10">
        <div class="n-max-width">
          <h2 class="text-3xl sm:text-4xl tracking-tight font-extrabold text-gray-900">
            Mes contacts
          </h2>
          <div class="mt-10">

            <ul role="list" class="contact-list">
              <li v-for="contact in $store.account.account.contacts" :key="contact.id">
                <div class="w-full flex items-center justify-between p-6 space-x-6">
                  <div class="flex-1 truncate">
                    <div class="flex items-center space-x-3">
                      <div class="text-gray-900 text-sm font-medium truncate">
                        {{ contact.firstname }} {{ contact.lastname }}
                      </div>
                    </div>
                    <p class="mt-1 text-gray-500 text-sm truncate">
                      {{ $enum.UserRole.translate(contact.role) }}
                    </p>
                  </div>
                  <UserIcon :user="contact" />
                </div>
                <div class="contact-links">
                  <a :href="`mailto:${contact.email}`">
                    <EnvelopeIcon aria-hidden="true" />
                    <span>&nbsp;</span>
                  </a>
                  <a v-if="contact.phone" :href="`tel:${contact.phone}`">
                    <PhoneIcon aria-hidden="true" />
                    <span>{{ contact.phone }}</span>
                  </a>
                  <a v-if="contact.link" target="_blank" :href="contact.link" title="Prendre RDV">
                    <CalendarIcon aria-hidden="true" />
                    <span>&nbsp;</span>
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

    </main>

  </div>
</template>

<script lang="ts">
import { CheckIcon, EnvelopeIcon, PhoneIcon, CalendarIcon } from '@heroicons/vue/24/outline'

import type { IComment } from '@/api/myedvenn/comment'

export default defineComponent({
  components: {
    CheckIcon, EnvelopeIcon, PhoneIcon, CalendarIcon
  },
  setup() {
    useSeoMeta({ title: 'My Edvenn - Accueil' })

    const { $api, $store } = useNuxtApp()

    const commentsCount = ref(0)
    const commentsOffset = ref(0)
    const comments = ref<null | IComment[]>(null)

    watch(commentsOffset, offset => {
      $api.comment.find({
        limit: 3,
        offset,
        relations: [
          'user',
          'packageFile.package',
          'contactTrainingModule.packageTrainingModule'
        ],
        select: [
          '*',
          'user.id',
          'packageFile.package.id',
          'contactTrainingModule.packageTrainingModule.name'
        ]
      }).then(c => {
        commentsCount.value = c.count
        comments.value = c.values
      })
    }, { immediate: $store.account.rights.isTrainee })

    return {
      comments,
      commentsCount,
      commentsOffset,

      packages: computed(() =>
        $store.trainee.contactPackages
          .map(t => ({
            ...t,
            package: $store.cache.getPackage(t.packageId),
            contactPackage: $store.cache.getContactPackage(t.contactPackageId),
          }))
          .filter(t => t.package && t.contactPackage)
      )
    }
  }
})
</script>

<style lang="postcss">
ul.contact-list {
  @apply grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3;

  >li {
    @apply col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200;

    >.contact-links {
      @apply w-full grid grid-flow-col auto-cols-auto divide-x;

      >a {
        @apply mx-1 flex flex-col items-center justify-center py-4 text-sm text-gray-700 font-medium hover:text-gray-500;

        >svg {
          @apply w-5 h-5 text-gray-400;
        }
      }
    }
  }
}
</style>